import React from 'react';
import {
  Box, Typography, alpha, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Chip, TextField, InputAdornment,
  ToggleButton, ToggleButtonGroup,
} from '@mui/material';
import { ArrowDownward, Search } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import {
  LEVEL_FILTERS, EmptyState, LevelBadge, ScoreBar, SignalHighlights,
  TableSkeleton, fmtPct, getCloseSummary, getDistributionSummary,
  getMaSummary, getSectorLabel, getStrengthSummary, getVolumeSummary,
  levelColor, thSx, tdSx,
} from './shared';

const LeaderboardSection = ({
  data,
  filtered,
  loading,
  summary,
  filter,
  onFilterChange,
  search,
  onSearchChange,
  sortKey,
  sortDir,
  onSort,
  onSelectTicker,
}) => {
  const { t, i18n } = useTranslation();

  const SortHeader = ({ sortField, label, align }) => (
    <TableCell
      sx={{ ...thSx, cursor: 'pointer', userSelect: 'none', '&:hover': { color: '#475569' } }}
      align={align}
      onClick={() => onSort(sortField)}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3, justifyContent: align === 'right' ? 'flex-end' : 'flex-start' }}>
        {label}
        {sortKey === sortField && (
          sortDir === 'desc'
            ? <ArrowDownward sx={{ fontSize: 12 }} />
            : <ArrowDownward sx={{ fontSize: 12, transform: 'rotate(180deg)' }} />
        )}
      </Box>
    </TableCell>
  );

  return (
    <>
      {loading && !summary && <TableSkeleton />}

      {data.length > 0 && (
        <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap', opacity: loading ? 0.5 : 1, transition: 'opacity 0.2s' }}>
          <ToggleButtonGroup
            value={filter}
            exclusive
            onChange={(_, value) => value && onFilterChange(value)}
            size="small"
            sx={{ '& .MuiToggleButton-root': { textTransform: 'none', fontSize: '0.72rem', fontWeight: 600, py: 0.4, px: 1.2, borderColor: '#e2e8f0' } }}
          >
            {LEVEL_FILTERS.map((item) => (
              <ToggleButton
                key={item.key}
                value={item.key}
                sx={{ '&.Mui-selected': { bgcolor: alpha(item.color, 0.08), color: item.color, borderColor: alpha(item.color, 0.3) } }}
              >
                <item.icon sx={{ fontSize: 14, mr: 0.4 }} />
                {t(item.i18n)}
                {filter === 'all' && item.key !== 'all' && summary && (
                  <Typography component="span" sx={{ ml: 0.4, fontSize: '0.65rem', opacity: 0.7 }}>
                    ({item.key === 'SELL' ? summary.level_4_count : item.key === 'REDUCE' ? summary.level_3_count : item.key === 'MONITOR' ? summary.level_2_count : summary.level_1_count || 0})
                  </Typography>
                )}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>

          <TextField
            size="small"
            placeholder={t('distribution.searchPlaceholder')}
            value={search}
            onChange={(event) => onSearchChange(event.target.value)}
            InputProps={{
              startAdornment: <InputAdornment position="start"><Search sx={{ fontSize: 16, color: '#94a3b8' }} /></InputAdornment>,
              sx: { fontSize: '0.78rem', bgcolor: '#fff', borderRadius: 1.5, '& fieldset': { borderColor: '#e2e8f0' } },
            }}
            sx={{ width: 160 }}
          />

          <Typography sx={{ fontSize: '0.72rem', color: '#94a3b8', ml: 'auto' }}>
            {t('distribution.stocksCount', { filtered: filtered.length, total: data.length })}
          </Typography>
        </Box>
      )}

      {data.length > 0 && (
        <Box sx={{ opacity: loading ? 0.5 : 1, transition: 'opacity 0.2s' }}>
          <Box sx={{ bgcolor: '#fff', borderRadius: 2, border: '1px solid #e2e8f0', overflow: 'hidden' }}>
            {filtered.length === 0 ? (
              <EmptyState title={t('distribution.noData')} subtitle={t('distribution.noDataSub')} />
            ) : (
              <TableContainer>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={thSx}>{t('distribution.table.ticker')}</TableCell>
                      <SortHeader sortField="total_dist_score" label={t('distribution.table.assessment')} align="left" />
                      <SortHeader sortField="active_signal_count" label={t('distribution.table.technical')} align="left" />
                      <TableCell sx={thSx}>{t('distribution.table.highlights')}</TableCell>
                      <SortHeader sortField="pct_change" label={t('distribution.table.priceTrend')} align="right" />
                      <SortHeader sortField="vol_ratio" label={t('distribution.table.flowStrength')} align="left" />
                      <SortHeader sortField="rs_5" label={t('distribution.table.relativeStrength')} align="left" />
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filtered.map((row) => {
                      const maSummary = getMaSummary(row, t);
                      const volumeSummary = getVolumeSummary(row.vol_ratio, t);
                      const strengthSummary = getStrengthSummary(row.rs_5, t);

                      return (
                        <TableRow
                          key={row.ticker}
                          onClick={() => onSelectTicker(row)}
                          sx={{
                            cursor: 'pointer',
                            '&:nth-of-type(even)': { bgcolor: '#fafbfc' },
                            '&:hover': { bgcolor: alpha('#3b82f6', 0.04) },
                            borderLeft: `3px solid ${levelColor(row.dist_level)}`,
                          }}
                        >
                          <TableCell sx={{ ...tdSx, minWidth: 170 }}>
                            <Typography sx={{ fontWeight: 800, fontSize: '0.82rem', color: '#1e293b', lineHeight: 1.1 }}>{row.ticker}</Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4, flexWrap: 'wrap', mt: 0.35 }}>
                              {row.sector_code && (
                                <Chip label={getSectorLabel(row, i18n.language)} size="small" sx={{ height: 16, fontSize: '0.52rem', fontWeight: 700, bgcolor: '#eff6ff', color: '#2563eb' }} />
                              )}
                            </Box>
                          </TableCell>
                          <TableCell sx={{ ...tdSx, minWidth: 175 }}>
                            <Box sx={{ mb: 0.22 }}>
                              <ScoreBar score={row.total_dist_score} />
                            </Box>
                            <Typography sx={{ fontSize: '0.58rem', color: '#94a3b8' }}>
                              {getDistributionSummary(row.dist_count, t)}
                            </Typography>
                          </TableCell>
                          <TableCell sx={{ ...tdSx, minWidth: 175 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6, flexWrap: 'wrap', mb: 0.28 }}>
                              <LevelBadge level={row.dist_level} />
                              <Typography sx={{ fontSize: '0.58rem', color: '#94a3b8', fontWeight: 700 }}>
                                {t('distribution.table.signalsCount', { count: row.active_signal_count ?? 0 })}
                              </Typography>
                              <Typography sx={{ fontSize: '0.58rem', color: '#7c3aed', fontWeight: 700 }}>
                                {t('distribution.table.patternsCompact', { count: row.active_pattern_count ?? 0 })}
                              </Typography>
                            </Box>
                            <Typography sx={{ fontSize: '0.56rem', fontWeight: 700, color: maSummary.color }}>
                              {maSummary.label}
                            </Typography>
                          </TableCell>
                          <TableCell sx={{ ...tdSx, minWidth: 180 }}>
                            <SignalHighlights row={row} />
                          </TableCell>
                          <TableCell sx={{ ...tdSx, minWidth: 170, fontFamily: 'monospace' }} align="right">
                            <Typography sx={{ fontSize: '0.76rem', fontWeight: 700, color: '#334155' }}>
                              {row.close ? Number(row.close).toLocaleString() : '—'}
                            </Typography>
                            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 0.45, mt: 0.16, flexWrap: 'wrap' }}>
                              <Typography sx={{ fontSize: '0.62rem', fontWeight: 700, color: row.pct_change < 0 ? '#ef4444' : '#10b981' }}>
                                {fmtPct(row.pct_change)}
                              </Typography>
                              <Typography sx={{ fontSize: '0.56rem', color: row.ret_5 < 0 ? '#ef4444' : '#10b981' }}>
                                5D {fmtPct(row.ret_5)}
                              </Typography>
                              <Typography sx={{ fontSize: '0.56rem', color: row.ret_10 < 0 ? '#ef4444' : '#10b981' }}>
                                10D {fmtPct(row.ret_10)}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell sx={{ ...tdSx, minWidth: 165 }}>
                            <Typography sx={{ fontSize: '0.68rem', fontWeight: 800, color: volumeSummary.color, lineHeight: 1.1 }}>
                              {volumeSummary.label}
                            </Typography>
                            <Typography sx={{ fontSize: '0.58rem', color: '#94a3b8', mt: 0.18 }}>
                              {t('distribution.table.volumeRatioCompact', { value: row.vol_ratio ? row.vol_ratio.toFixed(2) : '—' })}
                            </Typography>
                            <Typography sx={{ fontSize: '0.58rem', color: '#94a3b8', mt: 0.18 }}>
                              {getCloseSummary(row.close_pos, t)}
                            </Typography>
                          </TableCell>
                          <TableCell sx={{ ...tdSx, minWidth: 150 }}>
                            <Typography sx={{ fontSize: '0.68rem', color: strengthSummary.color, fontWeight: 800, lineHeight: 1.1 }}>
                              {strengthSummary.label}
                            </Typography>
                            <Typography sx={{ fontSize: '0.58rem', color: row.rs_5 < 0 ? '#ef4444' : '#10b981', mt: 0.18, fontWeight: 700, fontFamily: 'monospace' }}>
                              {t('distribution.table.rs5Compact', { value: fmtPct(row.rs_5) })}
                            </Typography>
                            <Typography sx={{ fontSize: '0.58rem', color: row.ret_10 < 0 ? '#ef4444' : '#10b981', mt: 0.18, fontFamily: 'monospace' }}>
                              {t('distribution.table.ret10Compact', { value: fmtPct(row.ret_10) })}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Box>
        </Box>
      )}
    </>
  );
};

export default LeaderboardSection;